/** Форма статьи и версии: текст, владелец, роли, срок пересмотра, заменённые части (БЗ-07, БЗ-09, БЗ-11) */
import { KB_SECTIONS, type KbReplacedPart, type KbSection } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { api } from '../../lib/api';
import { Button, IconButton } from '../../ui/button';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { metaQuery } from './shared';

export interface VersionDraft {
  title: string;
  summary: string;
  body: string;
  changeNote: string;
  replacedParts: KbReplacedPart[];
}

export interface CardDraft {
  section: KbSection;
  ownerId: string;
  ownerUnit: string;
  visibleRoles: string[];
  reviewEveryMonths: string;
}

const headingsOf = (body: string) => [...body.matchAll(/^#{1,6}\s+(.*\S)\s*$/gm)].map((m) => m[1]!.trim());

export function VersionFields({
  value,
  onChange,
  articleId,
  showNote,
}: {
  value: VersionDraft;
  onChange: (v: VersionDraft) => void;
  articleId?: string;
  showNote?: boolean;
}) {
  const heads = useMemo(() => headingsOf(value.body), [value.body]);
  const articles = useQuery({
    queryKey: ['kb', 'articles', '', ''],
    queryFn: () => api<Array<{ id: string; number: string; title: string }>>('/kb/articles'),
  });
  const set = (p: Partial<VersionDraft>) => onChange({ ...value, ...p });
  const others = (articles.data ?? []).filter((a) => a.id !== articleId);
  return (
    <div className="flex flex-col gap-4">
      <Field label="Название" htmlFor="kb-title">
        <Input
          id="kb-title"
          value={value.title}
          onChange={(e) => set({ title: e.target.value })}
          maxLength={300}
        />
      </Field>
      <Field label="Коротко: о чём статья" htmlFor="kb-summary" hint="Одна-две строки для списка и поиска">
        <Input
          id="kb-summary"
          value={value.summary}
          onChange={(e) => set({ summary: e.target.value })}
          maxLength={1000}
        />
      </Field>
      <Field
        label="Текст"
        htmlFor="kb-body"
        hint="Заголовки — строки с # в начале (## Раздел), списки — строки с «-» или «1.», жирный — **так**. Заменённую часть отмечают по заголовку."
      >
        <Textarea
          id="kb-body"
          value={value.body}
          onChange={(e) => set({ body: e.target.value })}
          rows={16}
          className="font-mono text-[13px]"
        />
      </Field>
      {showNote ? (
        <Field label="Что изменилось в этой версии" htmlFor="kb-note">
          <Input
            id="kb-note"
            value={value.changeNote}
            onChange={(e) => set({ changeNote: e.target.value })}
            maxLength={1000}
          />
        </Field>
      ) : null}
      <fieldset className="rounded-md border border-line p-3">
        <legend className="px-1 text-[13px] font-medium text-ink">Заменённые части</legend>
        <p className="mb-2 text-[12px] text-ink-2">
          Если часть текста заменена более поздней статьёй, укажите её заголовок и статью-замену. В статье она
          будет помечена «заменено», а Pilot по ней отвечать не будет.
        </p>
        {value.replacedParts.map((p, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: строки формы без устойчивого ключа
          <div key={i} className="mb-3 grid gap-2 md:grid-cols-[1fr_1fr_9rem_auto] md:items-end">
            <Field label="Заголовок части">
              <Select
                aria-label="Заголовок заменённой части"
                value={p.label}
                onChange={(e) =>
                  set({
                    replacedParts: value.replacedParts.map((x, j) =>
                      j === i ? { ...x, label: e.target.value } : x,
                    ),
                  })
                }
              >
                <option value="">— выберите заголовок —</option>
                {heads.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Чем заменена">
              <Select
                aria-label="Статья-замена"
                value={p.replacedById}
                onChange={(e) =>
                  set({
                    replacedParts: value.replacedParts.map((x, j) =>
                      j === i ? { ...x, replacedById: e.target.value } : x,
                    ),
                  })
                }
              >
                <option value="">— статья —</option>
                {others.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.number} {a.title}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="С какой даты">
              <Input
                type="date"
                value={p.since}
                onChange={(e) =>
                  set({
                    replacedParts: value.replacedParts.map((x, j) =>
                      j === i ? { ...x, since: e.target.value } : x,
                    ),
                  })
                }
              />
            </Field>
            <IconButton
              label="Убрать заменённую часть"
              onClick={() => set({ replacedParts: value.replacedParts.filter((_, j) => j !== i) })}
            >
              <Trash2 className="size-4" />
            </IconButton>
          </div>
        ))}
        <Button
          size="sm"
          icon={<Plus className="size-4" />}
          onClick={() =>
            set({
              replacedParts: [
                ...value.replacedParts,
                { label: '', replacedById: '', since: new Date().toISOString().slice(0, 10) },
              ],
            })
          }
        >
          Добавить заменённую часть
        </Button>
      </fieldset>
    </div>
  );
}

export function CardFields({ value, onChange }: { value: CardDraft; onChange: (v: CardDraft) => void }) {
  const meta = useQuery(metaQuery);
  const people = useQuery({
    queryKey: ['people'],
    queryFn: () => api<Array<{ id: string; fullName: string; position: string | null }>>('/people'),
  });
  const set = (p: Partial<CardDraft>) => onChange({ ...value, ...p });
  const toggleRole = (code: string) =>
    set({
      visibleRoles: value.visibleRoles.includes(code)
        ? value.visibleRoles.filter((r) => r !== code)
        : [...value.visibleRoles, code],
    });
  return (
    <div className="flex flex-col gap-4">
      <Field label="Раздел базы знаний" htmlFor="kb-section">
        <Select
          id="kb-section"
          value={value.section}
          onChange={(e) => set({ section: e.target.value as KbSection })}
        >
          {(Object.keys(KB_SECTIONS) as KbSection[]).map((s) => (
            <option key={s} value={s}>
              {KB_SECTIONS[s]}
            </option>
          ))}
        </Select>
      </Field>
      <div className="grid gap-4 md:grid-cols-2">
        <Field
          label="Владелец статьи"
          htmlFor="kb-owner"
          hint="Проверяет и публикует версии, получает задачу пересмотра"
        >
          <Select id="kb-owner" value={value.ownerId} onChange={(e) => set({ ownerId: e.target.value })}>
            <option value="">Без владельца (публикует администратор)</option>
            {(people.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
                {p.position ? ` — ${p.position}` : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Подразделение-владелец" htmlFor="kb-unit" hint="Например «сметный отдел»">
          <Input
            id="kb-unit"
            value={value.ownerUnit}
            onChange={(e) => set({ ownerUnit: e.target.value })}
            maxLength={120}
          />
        </Field>
      </div>
      <Field
        label="Срок пересмотра, месяцев"
        htmlFor="kb-review"
        hint="Когда срок выйдет, владелец получит дело в «Ждёт меня»"
      >
        <Input
          id="kb-review"
          type="number"
          min={1}
          max={60}
          value={value.reviewEveryMonths}
          onChange={(e) => set({ reviewEveryMonths: e.target.value })}
          className="md:w-40"
        />
      </Field>
      <fieldset>
        <legend className="mb-1 text-[13px] font-medium text-ink">Кому видна статья</legend>
        <p className="mb-2 text-[12px] text-ink-2">Ничего не отмечено — видна всем сотрудникам компании.</p>
        <div className="grid gap-x-4 md:grid-cols-2">
          {(meta.data?.allRoles ?? []).map((r) => (
            <label
              key={r.code}
              className="flex min-h-11 cursor-pointer items-center gap-3 border-b border-line/60 py-1.5 text-[14px] md:min-h-9"
            >
              <input
                type="checkbox"
                className="size-5 shrink-0 md:size-4"
                checked={value.visibleRoles.includes(r.code)}
                onChange={() => toggleRole(r.code)}
              />
              {r.name}
            </label>
          ))}
        </div>
      </fieldset>
    </div>
  );
}
